import {adjustFromTimeZoneSafely, ServiceEventEntity} from "@ots-frontend/ots-data";
import React, {useCallback, useRef, useState} from "react";
import {HeatmapLayer} from "@react-google-maps/api";
import {useServiceEventHeatmap} from "../../../hooks/useServiceEventHeatmap";
import {HeatmapTimeline} from "../../timeline/HeatmapTimeline/HeatmapTimeline";
import styled from "@emotion/styled";
import {Divider, Group, Loader, Paper, Text} from "@mantine/core";
import {useDebounce} from "react-use";
import {
  InfoWindowDetail,
  isOpsMapDetail,
  Map as MapComponent,
  MAP_DEFAULT_BOUNDS,
  MAP_DEFAULT_CENTER,
  MapDetail,
  useMapLoader
} from "../../map/Map/Map";
import {useOpsStatuses} from "../../../hooks/useOpsStatuses";
import {OpsLocationStatus} from "../../../model/ops-status";
import {ResizeableContent} from "../../ResizableContent/ResizableContent";
import {OpsStatusInfoWindow} from "../../map/info-window/OpsStatusInfoWindow/OpsStatusInfoWindow";
import {OpsStatusMarkerClusterer} from "../../map/marker/OpsStatusMarkerCluster/OpsStatusMarkerCluster";
import {QuickPanelContainer} from "../../map/panel/QuickPanelContainer/QuickPanelContainer";
import {OpsStatusInfoPanel} from "../../map/panel/info-panels/OpsStatusInfoPanel/OpsStatusInfoPanel";
import {useTranslation} from "react-i18next";
import { Switch } from "../../switch/Switch/Switch";

export type ServiceEventMapProps = {
  serviceEvent: ServiceEventEntity
}

const DEFAULT_MAP_OPTIONS: google.maps.MapOptions = {
  mapTypeId: "satellite",
  mapTypeControl: false,
  streetViewControl: false
};

export const ServiceEventMap = (props: ServiceEventMapProps) => {
  const {serviceEvent} = props

  const mapRef = useRef<google.maps.Map | null>(null);

  const [mapZoom, _setMapZoom] = useState<number>(8);
  const [mapCenter, _setMapCenter] = useState<google.maps.LatLngLiteral>(MAP_DEFAULT_CENTER);

  const [popupWindow, setPopupWindow] = useState<InfoWindowDetail | undefined>(undefined);
  const [hoverPopupWindow, setHoverPopupWindow] = useState<InfoWindowDetail | undefined>(undefined);

  const [endTime, setEndTime] = useState<Date | number>(adjustFromTimeZoneSafely(serviceEvent.endDttm) ?? new Date());
  const [debouncedEndTime, setDebouncedEndTime] = useState<Date | number>(adjustFromTimeZoneSafely(serviceEvent.endDttm) ?? new Date());

  const [heatmap, setHeatmap] = useState<boolean>(true);

  useDebounce(
    () => {
      setDebouncedEndTime(endTime);
    },
    300,
    [endTime]
  );

  const {isLoaded: isMapLoaded} = useMapLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  });

  const {
    heatmapData,
    isFetching,
    onMapLoad,
    onMapUnmount,
    onIdle,
  } = useServiceEventHeatmap({
    serviceEvent: serviceEvent,
    toDttm: debouncedEndTime,
  })

  const {
    opsLocationStatuses,
    isLoading: isOpsStatusLoading,
    isFetching: isOpsStatusFetching,
    error: opsStatusError,
  } = useOpsStatuses({
    dttm: debouncedEndTime,
    regionId: serviceEvent.regionId,
    serviceEventType: serviceEvent.eventType,
    serviceEventId: serviceEvent.id,
    pollingInterval: 30000,
  })

  const _onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    map.fitBounds(MAP_DEFAULT_BOUNDS);
    onMapLoad(map)
  }, [onMapLoad]);

  const _onMapUnmount = useCallback((map: google.maps.Map) => {
    mapRef.current = undefined;
    onMapUnmount(map)
  }, [onMapUnmount]);

  const _onIdle = useCallback(() => {
    onIdle()
  }, [onIdle])

  const onHoverOut = useCallback((_event: google.maps.MapMouseEvent) => {
    setHoverPopupWindow(undefined);
  }, []);

  const onHover = useCallback((event: google.maps.MapMouseEvent, detail: MapDetail) => {
      if (event.latLng) {
        setHoverPopupWindow({detail: detail, latLng: event.latLng});
      } else {
        setHoverPopupWindow(undefined);
      }
    }, []
  );

  const onOpsHover = useCallback((event: google.maps.MapMouseEvent, context: OpsLocationStatus) => {
      onHover(event, {mode: 'ops', context: context});
    }, [onHover]
  );

  const onMarkerClick = useCallback(
    (event: google.maps.MapMouseEvent, detail: MapDetail) => {
      if (event.latLng) {
        setPopupWindow({detail: detail, latLng: event.latLng});
      } else {
        setPopupWindow(undefined);
      }
      onHover(event, detail);
    },
    [onHover]
  );

  const onOpsMarkerClick = useCallback(
    (event: google.maps.MapMouseEvent, context: OpsLocationStatus) => {
      onMarkerClick(event, {mode: 'ops', context: context});
    },
    [onMarkerClick]
  );

  const clearPopup = useCallback(() => {
    setPopupWindow(undefined)
  }, [])

  const renderEntityPopup = useCallback((window: InfoWindowDetail | undefined) => {
      if (window) {
        if (isOpsMapDetail(window.detail)) {
          return (
            <OpsStatusInfoWindow
              ctx={window.detail.context}
              position={window.latLng}
              onCloseClick={() => setPopupWindow(undefined)}
            />
          );
        }
      }

      return undefined
    },
    []
  );

  const renderHoverEntityPopup = useCallback(() => {
    return renderEntityPopup(hoverPopupWindow);
  }, [renderEntityPopup, hoverPopupWindow]);

  const renderMarkerEntityPopup = useCallback(() => {
    return renderEntityPopup(popupWindow);
  }, [renderEntityPopup, popupWindow]);

  if (!isMapLoaded) {
    return <div/>;
  }

  return (
    <ResizeableContent>
      {(size) => (
        <MapComponent
          onLoad={_onMapLoad}
          onUnmount={_onMapUnmount}
          onIdle={_onIdle}
          zoom={mapZoom}
          center={mapCenter}
          mapContainerStyle={{width: size.width ?? "100%", height: size.height ?? "100%"}}
          options={DEFAULT_MAP_OPTIONS}
        >

          <TopPanelContainer>
            <Paper p="xs">
              <HeatmapTimeline
                initialStartTime={serviceEvent.startDttm}
                initialEndTime={serviceEvent.endDttm}
                onEndTimeChange={setEndTime}/>

              <Divider variant="dashed"/>

              <Group position="left" spacing="xs" mt="xs">
                <Switch
                  checked={heatmap}
                  onChange={(e) => setHeatmap(e.target.checked)}
                  size="xs"
                  label="Heatmap"
                />
                <Loader size="xs" color="gray" visibility={(isFetching || isOpsStatusFetching) ? "visible" : "hidden"}/>
              </Group>
            </Paper>
          </TopPanelContainer>

          <OpsStatusMarkerClusterer
            locs={opsLocationStatuses}
            onHover={onOpsHover}
            onHoverOut={onHoverOut}
            onMarkerClick={onOpsMarkerClick}/>
          {(heatmap && heatmapData.length > 0) && (
            <HeatmapLayer
              data={heatmapData}
              options={{
                radius: 20,
                opacity: 0.75,
                dissipating: true,
              }}
            />
          )}

          {renderHoverEntityPopup()}
          {renderMarkerEntityPopup()}
          {popupWindow &&
            <>
              {isOpsMapDetail(popupWindow.detail) &&
                <QuickPanelContainer onCloseClick={clearPopup}>
                  <OpsStatusInfoPanel site={popupWindow.detail.context}/>
                </QuickPanelContainer>
              }
            </>
          }
        </MapComponent>
      )}
    </ResizeableContent>
  );
};

const TopPanelContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  top: 0;
  left: 0;
  width: 85%;
`;
